var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { Component, Emit, Inject, Prop, Vue, } from "vue-property-decorator";
import ArticleAttributes from "../../components/articleattributes/articleattributes.vue";
import ColorLegend from "../../components/colorlegend.vue";
import ExpandedGeometry from "../../components/expandedgeometry.vue";
import Gltf from "../../components/gltf.vue";
import InfoIcon from "../../components/articleattributes/infoicon.vue";
import TechnicalDrawingsDialog from "../../components/dialog/technicaldrawingsdialog.vue";
import { createPngImage, createSvg, } from "../utilities/aux";
import { isManufacturablePart, isManufacturePart, isPurchasedPart, isUserDefinedTube, PartState, } from "../utilities/documentdialog";
import { isManufacturingStateEntryType, } from "../utilities/graph";
import { Level, TubeProfileGeometryType, } from "../utilities/wsi4_interfaces";
import { isTubeProfileGeometryCircular, isTubeProfileGeometryRectangular, } from "../utilities/wsi4_typeguards";
const backgroundFill = "#c0c0c0";
function distanceToBig(first, second) {
    return Math.abs(first - second) > 1e-2;
}
function tubeMeasurementText(t, i18n) {
    const numberText = (text) => i18n.numberWithTwoDecimals(text) + "mm";
    const text = i18n.t("tubeDimX") + ": " + numberText(t.dimX) + "\t";
    if (t.profile !== undefined) {
        return text + i18n.t("profile") + ": " + t.profile.name;
    }
    switch (t.geometry.type) {
        case TubeProfileGeometryType.rectangular: {
            if (!isTubeProfileGeometryRectangular(t.geometry.content)) {
                throw Error("Wrong type.");
            }
            return text + i18n.t("rectangular") + ": " + numberText(t.geometry.content.dimY) + " x " + numberText(t.geometry.content.dimZ) + " x " + numberText(t.geometry.content.thickness);
        }
        case TubeProfileGeometryType.circular: {
            if (!isTubeProfileGeometryCircular(t.geometry.content)) {
                throw Error("Wrong type.");
            }
            return text + i18n.t("circular") + ": " + i18n.t("outerRadius") + ": " + numberText(t.geometry.content.outerRadius) + " " + i18n.t("thickness") + ": " + numberText(t.geometry.content.thickness);
        }
    }
}
function changeFillAttribute(element, newFill) {
    for (const p of element.querySelectorAll("path")
        .values()) {
        if (p.hasAttribute("fill")) {
            const desc = p.getAttribute("d");
            if (desc === undefined || desc === null) {
                throw Error("Missing path description");
            }
            const closed = desc.lastIndexOf("Z");
            if (closed !== -1) {
                p.setAttribute("fill", newFill);
            }
        }
    }
}
function createPattern(degree) {
    const length = 512;
    const numberOfLines = length / 64;
    const radDegree = (360 - (-degree) / 360.) * 2. * Math.PI;
    const sin = Math.sin(radDegree);
    const cos = Math.cos(radDegree);
    const rotate = (x, y) => [
        cos * x - sin * y,
        sin * x + cos * y,
    ];
    const line = (x1, y1, x2, y2) => [
        rotate(x1, y1),
        rotate(x2, y2),
    ];
    const stroke = "#000";
    const strokeWidth = "1.5";
    const patternName = "grainPattern" + degree.toString();
    const pattern = document.createElementNS("http://www.w3.org/2000/svg", "pattern");
    pattern.setAttribute("id", patternName);
    pattern.setAttribute("width", length.toFixed());
    pattern.setAttribute("height", length.toFixed());
    pattern.setAttribute("patternUnits", "userSpaceOnUse");
    {
        const r = document.createElementNS("http://www.w3.org/2000/svg", "rect");
        r.setAttribute("width", length.toFixed());
        r.setAttribute("height", length.toFixed());
        r.setAttribute("stroke-width", "0");
        r.setAttribute("fill", backgroundFill);
        pattern.appendChild(r);
    }
    {
        const gH = document.createElementNS("http://www.w3.org/2000/svg", "g");
        gH.setAttribute("stroke", stroke);
        gH.setAttribute("stroke-width", strokeWidth);
        let lines = [];
        for (let i = 0; i < numberOfLines; ++i) {
            if (i % 2 === 0) {
                lines.push(line(length / numberOfLines, length * i / numberOfLines, length * 7 / numberOfLines, length * i / numberOfLines));
            }
            else {
                lines.push(line(0, length * i / numberOfLines, length * 3 / numberOfLines, length * i / numberOfLines));
                lines.push(line(length * 5 / numberOfLines, length * i / numberOfLines, length * 11 / numberOfLines, length * i / numberOfLines));
            }
        }
        const bigM = 1e23;
        {
            const minXCoord = lines.reduce((r0, line) => Math.min(line.reduce((r1, point) => Math.min(point[0], r1), r0), r0), bigM);
            const maxXCoord = lines.reduce((r0, line) => Math.max(line.reduce((r1, point) => Math.max(point[0], r1), r0), r0), -bigM);
            const middlePointX = minXCoord + (maxXCoord - minXCoord) / 2;
            if (distanceToBig(middlePointX, length / 2)) {
                lines = lines.map(line => line.map(point => [
                    point[0] - (middlePointX - (length / 2)),
                    point[1],
                ]));
            }
        }
        {
            const minYCoord = lines.reduce((r0, line) => Math.min(line.reduce((r1, point) => Math.min(point[1], r1), r0), r0), bigM);
            const maxYCoord = lines.reduce((r0, line) => Math.max(line.reduce((r1, point) => Math.max(point[1], r1), r0), r0), -bigM);
            const middlePointY = minYCoord + (maxYCoord - minYCoord) / 2;
            if (distanceToBig(middlePointY, length / 2)) {
                lines = lines.map(line => line.map(point => [
                    point[0],
                    point[1] - (middlePointY - (length / 2)),
                ]));
            }
        }
        {
            const minXCoord = lines.reduce((r0, line) => Math.min(line.reduce((r1, point) => Math.min(point[0], r1), r0), r0), bigM);
            const maxXCoord = lines.reduce((r0, line) => Math.max(line.reduce((r1, point) => Math.max(point[0], r1), r0), r0), -bigM);
            const middlePointX = minXCoord + (maxXCoord - minXCoord) / 2;
            const minYCoord = lines.reduce((r0, line) => Math.min(line.reduce((r1, point) => Math.min(point[1], r1), r0), r0), bigM);
            const maxYCoord = lines.reduce((r0, line) => Math.max(line.reduce((r1, point) => Math.max(point[1], r1), r0), r0), -bigM);
            const middlePointY = minYCoord + (maxYCoord - minYCoord) / 2;
            if (distanceToBig(middlePointX, (length / 2)) || distanceToBig(middlePointY, (length / 2))) {
                throw Error(degree.toString() + " " + middlePointX.toString() + " " + middlePointY.toString() + " " + (length * 0.5).toString());
            }
        }
        for (const coord of lines) {
            const l = document.createElementNS("http://www.w3.org/2000/svg", "line");
            l.setAttribute("x1", coord[0][0].toFixed());
            l.setAttribute("y1", coord[0][1].toFixed());
            l.setAttribute("x2", coord[1][0].toFixed());
            l.setAttribute("y2", coord[1][1].toFixed());
            gH.appendChild(l);
        }
        pattern.appendChild(gH);
    }
    return pattern;
}
function insertGrainPattern(svg, degree) {
    const defs = document.createElementNS("http://www.w3.org/2000/svg", "defs");
    defs.appendChild(createPattern(degree));
    svg.prepend(defs);
    return svg;
}
function createSvgElement(svg, degree) {
    const s = createSvg(svg);
    if (degree !== undefined) {
        insertGrainPattern(s, degree);
    }
    return s;
}
let PartInformation = class PartInformation extends Vue {
    constructor() {
        super(...arguments);
        this.currentRotations = undefined;
        this.showTechDrawings = false;
        this.showSvgDialog = false;
        this.showGltfDialog = false;
    }
    articleDataChange(b) { return b; }
    updateSvg() {
        if (this.projectEntry.svg === undefined) {
            throw Error("svgElement not defined");
        }
        const noRestriction = this.currentRotations === undefined || this.currentRotations.length === 0 || Math.min(...this.currentRotations) < 0 || this.currentRotations[0] >= 180;
        {
            const s = this.$el.querySelector(".svgInsert");
            if (s === undefined || s === null) {
                throw Error("Selector '.svgInsert' does not match");
            }
            if (s.contains(this.svgElement)) {
                s.removeChild(this.svgElement);
            }
            this.svgElement = createSvgElement(this.projectEntry.svg, (noRestriction || this.currentRotations === undefined) ? undefined : Math.min(...this.currentRotations));
            s.appendChild(this.svgElement);
        }
        changeFillAttribute(this.svgElement, (noRestriction || this.currentRotations === undefined)
            ? backgroundFill
            : "url(#grainPattern" + Math.min(...this.currentRotations)
                .toString() + ")");
    }
    onRotationChange(rotation) {
        if (this.svgElement === undefined) {
            throw Error("svgElement not defined");
        }
        if (this.projectEntry.svg === undefined) {
            throw Error("svgElement not defined");
        }
        this.currentRotations = rotation;
        this.updateSvg();
    }
    toggleSvgDialog() {
        this.showSvgDialog = !this.showSvgDialog;
        this.updateSvg();
    }
    toggleGltfDialog() {
        this.showGltfDialog = !this.showGltfDialog;
        this.updateSvg();
    }
    onMessageHelp(key) {
        if (!isManufacturingStateEntryType(key)) {
            throw Error("onMessageHelp: Wrong type");
        }
        this.networkBroker.showMessageHelp(key);
    }
    showInfoIcon(key) {
        if (!isManufacturingStateEntryType(key)) {
            throw Error("showInfoIcon: Wrong type");
        }
        return this.networkBroker.configuration.showMessageMap[key];
    }
    get isPurchasedPart() { return isPurchasedPart(this.projectEntry); }
    get measurementString() {
        if (isPurchasedPart(this.projectEntry)) {
            return "";
        }
        if (isUserDefinedTube(this.projectEntry)) {
            return tubeMeasurementText(this.projectEntry, this.i18n);
        }
        if (!isManufacturePart(this.projectEntry)) {
            throw Error("measurementString: Wrong type.");
        }
        return this.i18n.numberWithTwoDecimals(this.projectEntry.width)
            + "mm x "
            + this.i18n.numberWithTwoDecimals(this.projectEntry.height)
            + "mm x "
            + this.i18n.numberWithTwoDecimals(this.projectEntry.thickness) + "mm";
    }
    get hasSvg() { return this.projectEntry.svg !== undefined; }
    get hasPng() { return this.projectEntry.png !== undefined; }
    get hasGltf() { return this.projectEntry.gltf !== undefined; }
    get pngImage() { return this.projectEntry.png === undefined ? "" : createPngImage(this.projectEntry.png); }
    get gltfImage() { return this.projectEntry.gltf === undefined ? "" : this.projectEntry.gltf; }
    get gltfGeometry() {
        return {
            tag: "gltf",
            gltf: this.gltfImage,
            Renderer3d: this.Renderer3d,
        };
    }
    get svgGeometry() {
        return {
            tag: "svg",
            svg: this.projectEntry.svg === undefined ? "" : this.projectEntry.svg,
        };
    }
    get warningMessages() {
        return [
            ...this.projectEntry.virtualManufacturingState,
            ...this.projectEntry.actualManufacturingState,
        ]
            .filter(m => m.level === Level.warning)
            .filter((value, index, self) => self.findIndex(g => g.message === value.message) === index)
            .map(m => ({
            message: m.message,
            key: m.type,
            visible: this.networkBroker.configuration.showMessageMap[m.type],
        }));
    }
    get errorMessages() {
        return [
            ...this.projectEntry.virtualManufacturingState,
            ...this.projectEntry.actualManufacturingState,
        ]
            .filter(m => (m.level === Level.fatalError) || (m.level === Level.recoverableError))
            .filter((value, index, self) => self.findIndex(g => g.message === value.message) === index)
            .map(m => ({
            message: m.message,
            key: m.type,
            visible: this.networkBroker.configuration.showMessageMap[m.type],
        }));
    }
    get showMessages() {
        return this.warningMessages.length !== 0 || this.errorMessages.length !== 0;
    }
    get manufacturable() { return isManufacturablePart(this.projectEntry) && this.projectEntry.partState !== PartState.notManufacturable; }
    get rotations() {
        if (!isManufacturablePart(this.projectEntry)) {
            return undefined;
        }
        if (this.projectEntry.fixedRotations === undefined) {
            return undefined;
        }
        return this.projectEntry.fixedRotations.rotations;
    }
    get technicalDrawings() {
        if (!isManufacturablePart(this.projectEntry)) {
            return undefined;
        }
        if (this.projectEntry.technicalDrawings.length === 0) {
            return undefined;
        }
        return this.projectEntry.technicalDrawings;
    }
    mounted() {
        if (this.projectEntry.svg !== undefined) {
            this.currentRotations = this.rotations;
            this.updateSvg();
        }
    }
    updated() {
        if (this.projectEntry.svg !== undefined) {
            this.updateSvg();
        }
    }
};
__decorate([
    Prop({ required: true })
], PartInformation.prototype, "projectEntry", void 0);
__decorate([
    Prop({ required: true })
], PartInformation.prototype, "Renderer3d", void 0);
__decorate([
    Prop({ required: true })
], PartInformation.prototype, "shopConfig", void 0);
__decorate([
    Emit()
], PartInformation.prototype, "articleDataChange", null);
__decorate([
    Inject()
], PartInformation.prototype, "i18n", void 0);
__decorate([
    Inject()
], PartInformation.prototype, "networkBroker", void 0);
PartInformation = __decorate([
    Component({
        components: {
            ArticleAttributes,
            ColorLegend,
            ExpandedGeometry,
            Gltf,
            InfoIcon,
            TechnicalDrawingsDialog,
        },
        name: "PartInformation",
    })
], PartInformation);
export default PartInformation;
